import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  MessageSquare, Send, Bot, User, Sparkles, Lightbulb,
  TrendingDown, AlertTriangle, ArrowRight, Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAdvisor = () => {
  const { shouldShowData } = useDemoData();

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickInsights, setQuickInsights] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchQuickInsights();
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: `👋 Hi! I'm your **CARTIEAI Advisor**. I can help you:\n\n• Understand your cloud spending across AWS, Azure, GCP, Snowflake, Databricks & DigitalOcean\n• Find cost savings opportunities\n• Answer questions about your infrastructure\n• Recommend optimization actions\n\nTry asking me something like:\n- "Why is my AWS bill so high?"\n- "What's my Snowflake spend this month?"\n- "How can I save money this month?"\n- "What are my biggest cost drivers?"`,
      timestamp: new Date().toISOString()
    }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchQuickInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai/quick-insights`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuickInsights(data.insights || []);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    }
  };

  const sendMessage = async (text = inputValue) => {
    if (!text.trim()) return;
    
    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: text,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        toast.error('Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/ai/chat/history/${sessionId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error clearing history:', error);
      }
    }
    setMessages([{
      role: 'assistant',
      content: `👋 Chat cleared! How can I help you with your cloud costs today?`,
      timestamp: new Date().toISOString()
    }]);
    setSessionId(null);
    toast.success('Chat history cleared');
  };

  const suggestedQuestions = [
    "How can I reduce costs?",
    "What's causing cost spikes?",
    "Show me savings opportunities",
    "Analyze my top spending services"
  ];

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Bullet points
        if (line.startsWith('• ') || line.startsWith('- ')) {
          return `<li key=${i} class="ml-4">${line.substring(2)}</li>`;
        }
        return `<p key=${i} class="mb-1">${line}</p>`;
      })
      .join('');
  };

  if (!shouldShowData) {
    return <EmptyDataState title="AIAdvisor" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="ai-advisor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-emerald-500" />
            AI Cost Advisor
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Ask anything about your cloud costs and get instant insights
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={clearChat}
          className="text-slate-400"
          data-testid="clear-chat-btn"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Clear Chat
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat Area */}
        <div className="lg:col-span-3">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="border-b bg-emerald-50 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bot className="h-5 w-5 text-emerald-600" />
                CARTIEAI
                <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">
                  Powered by GPT
                </Badge>
              </CardTitle>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-5 w-5 text-emerald-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                    }`}
                  >
                    <div 
                      className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                    />
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-slate-400 dark:text-gray-300" />
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                    <Bot className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </CardContent>

            {/* Suggested Questions */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-slate-400 mb-2">Suggested questions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      className="text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-full text-gray-700 dark:text-gray-300 transition-colors"
                      data-testid={`suggested-question-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t">
              <form 
                onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                className="flex gap-2"
              >
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask about your cloud costs..."
                  className="flex-1"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  disabled={isLoading || !inputValue.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="send-message-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          </Card>
        </div>

        {/* Quick Insights Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Quick Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quickInsights.length > 0 ? (
                quickInsights.map((insight, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg border ${
                      insight.type === 'savings' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                      insight.type === 'alert' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' :
                      'bg-emerald-50 dark:bg-blue-900/20 border-emerald-200 dark:border-blue-800'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{insight.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                          {insight.title}
                        </h4>
                        <p className="text-xs text-slate-400 dark:text-gray-400 mt-1">
                          {insight.description}
                        </p>
                        {insight.action && (
                          <a 
                            href={insight.actionLink}
                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium mt-2 inline-flex items-center gap-1"
                          >
                            {insight.action}
                            <ArrowRight className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  No insights yet. Start by adding some cost data!
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start text-sm"
                onClick={() => sendMessage("What are my top 3 savings opportunities?")}
              >
                <TrendingDown className="h-4 w-4 mr-2 text-green-500" />
                Find Savings
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-sm"
                onClick={() => sendMessage("Are there any cost anomalies I should know about?")}
              >
                <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                Check Anomalies
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-sm"
                onClick={() => sendMessage("Give me a summary of my cloud spending")}
              >
                <MessageSquare className="h-4 w-4 mr-2 text-emerald-500" />
                Cost Summary
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIAdvisor;
